import { RadioField } from '@hologram-dimension/radio-field';
import { useInstantSearch, useMenu } from 'react-instantsearch';
import { ProfilesDaoSimProfileDetails } from 'types/__generated__/SimInventoryApi';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import styles from './ProfileFilter.module.css';
import useAlgoliaFilter from '../useAlgoliaFilter';
import useAlgoliaMeta from '../useAlgoliaMeta';
import useCoverageProfiles from '../useCoverageProfiles';

interface ProfileFilterFormProps {
  profile: string;
}

interface SimProfileFilterProps {
  simProfiles?: ProfilesDaoSimProfileDetails[];
}

const SimProfileFilter = (props: SimProfileFilterProps) => {
  const { simProfiles } = props;
  const { refine, items } = useMenu({
    attribute: 'offer_id',
    limit: 100,
  });
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const profileFilter = indexUiState.menu?.offer_id ?? null;
  const filter = useAlgoliaFilter({ simProfiles });
  const { data: meta } = useAlgoliaMeta(filter);
  const { sendAnalyticsEvent } = useAnalytics();
  const { register, handleSubmit, watch, setValue } = useForm<ProfileFilterFormProps>({
    defaultValues: { profile: profileFilter ?? 'all' },
    mode: 'onChange',
  });
  const profiles = useCoverageProfiles({ simProfiles });

  const commitStateChange = useCallback<SubmitHandler<ProfileFilterFormProps>>(
    (data) => {
      const { profile } = data;

      if (profile === 'all') {
        if (sendAnalyticsEvent) {
          sendAnalyticsEvent('Network Coverage - Filter - All profiles');
        }
        return setIndexUiState((prevIndexUiState) => {
          const { menu, ...newState } = prevIndexUiState;
          return newState;
        });
      }
      if (sendAnalyticsEvent) {
        sendAnalyticsEvent('Network Coverage - Filter - Single Profile');
      }
      return refine(profile);
    },
    [refine, setIndexUiState, sendAnalyticsEvent]
  );

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit(commitStateChange)();
    });
    return () => subscription.unsubscribe();
  }, [commitStateChange, handleSubmit, watch]);

  useEffect(() => {
    if (profileFilter === null) {
      setValue('profile', 'all');
    }
  }, [profileFilter, setValue]);

  // We don't need to show this filter if there's only one profile
  if (!profiles || profiles.length <= 1) return null;

  const options: Array<{
    label: string;
    value: string;
    labelNode: React.ReactNode;
  }> = [];

  options.push({
    label: `This SIM`,
    value: 'all',
    labelNode: (
      <span>
        This SIM&nbsp;&nbsp;
        <span className={styles.diminishedText}>({meta && `${meta.networks}, `}all profiles)</span>
      </span>
    ),
  });

  profiles.map((profile) =>
    options.push({
      label: profile.profileName!,
      value: profile.offerId!.toString(),
      labelNode: (
        <span>
          {profile.profileName}
          &nbsp;&nbsp;
          <span className={styles.diminishedText}>
            ({items.find((item) => item.value === profile.offerId!.toString())?.count ?? 0}
            {profile.installationState === 'ENABLED' && ', active'})
          </span>
        </span>
      ),
    })
  );

  return <RadioField label="Coverage by profile" options={options} {...register('profile')} />;
};

export default SimProfileFilter;
